import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getOrderDetail } from "../../State/Order/Action";
import {
  ShoppingCart,
  UserCircle,
  CreditCard,
  Banknote,
  BadgeCheck,
  CalendarClock,
  ReceiptText,
  BookOpen,
  Hash,
} from "lucide-react";
import { Box, Paper, Typography, Grid, Divider, Avatar } from "@mui/material";
import { motion } from "framer-motion";

const OrderDetail = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const order = useSelector((store) => store.order.orderDetailResult?.data);

  useEffect(() => {
    dispatch(getOrderDetail(orderId));
  }, [dispatch, orderId]);

  if (!order) return <Typography>Loading...</Typography>;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: 4,
        background: "linear-gradient(135deg, #f0f4ff, #e0f7fa)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          <ReceiptText className="inline-block text-blue-600" /> Order Summary
        </Typography>
      </motion.div>

      <Paper elevation={4} sx={{ p: 3, borderRadius: 3, mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <Hash className="inline mr-1 text-blue-500" />{" "}
              <strong>Order Id:</strong> {order.orderId}
            </Typography>
            <Typography>
              <UserCircle className="inline mr-1 text-blue-500" />{" "}
              <strong>Learner:</strong> {order.learnerName}
            </Typography>
            <Typography>
              <CalendarClock className="inline mr-1 text-purple-500" />{" "}
              <strong>Created At:</strong>{" "}
              {new Date(order.createdAt).toLocaleString()}
            </Typography>
            <Typography>
              <BadgeCheck className="inline mr-1 text-indigo-500" />{" "}
              <strong>Status:</strong> {order.status}
            </Typography>
            <Typography>
              <ShoppingCart className="inline mr-1 text-orange-500" />{" "}
              <strong>Transaction ID:</strong> {order.transactionId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <Banknote className="inline mr-1 text-teal-500" />{" "}
              <strong>Subtotal:</strong> ₹{order.price}
            </Typography>
            <Typography>
              <Banknote className="inline mr-1 text-red-500" />{" "}
              <strong>Discount:</strong> ₹{order.discount}
            </Typography>
            <Typography>
              <Banknote className="inline mr-1 text-green-700" />{" "}
              <strong>Total:</strong> ₹{order.totalPrice}
            </Typography>
            <Typography>
              <CreditCard className="inline mr-1 text-pink-500" />{" "}
              <strong>Currency:</strong> {order.currency}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h5" mt={5} mb={2} fontWeight="bold">
        <BookOpen className="inline-block mr-1 text-blue-700" /> Courses in this
        Order
      </Typography>

      <Grid container spacing={2} direction="column">
        {order.orderItems.map((item, idx) => (
          <Grid item xs={12} key={item.courseId}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Paper
                elevation={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Avatar
                  src={item.courseThumbnailUrl}
                  variant="rounded"
                  sx={{ width: 80, height: 80, mr: 2 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{item.courseName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <UserCircle className="inline text-sm mr-1 text-indigo-600" />{" "}
                    {item.courseInstructorName}
                  </Typography>
                  <Box
                    sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}
                  >
                    <Typography variant="body2">
                      <Banknote className="inline text-teal-500 mr-1" />{" "}
                      Price: ₹{item.price}
                    </Typography>
                    <Typography variant="body2">
                      <Banknote className="inline text-red-500 mr-1" />{" "}
                      Discount: ₹{item.discount}
                    </Typography>
                    <Typography variant="body2">
                      <Banknote className="inline text-green-600 mr-1" />{" "}
                      Total: ₹{item.totalPrice}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default OrderDetail;
